import { useEffect, useRef, useState } from 'react';

import {
  Container,
  NavigationHeaderContainer,
  NavigationItemsContainer,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/shared';
import StepNavItem from 'dashboard/apps/InquiryTemplateEditor/StepNavItem';
import { InquiryTemplateVersionStep } from 'dashboard/models/inquiry-template-version';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';

interface InquiryTemplateSimulateStepsNavigationProps {
  currentStepName: string;
  steps: InquiryTemplateVersionStep[];
  onSelectStep: (step: string) => void;
}

export const InquiryTemplateStepsPanelWidth = 300;

export const InquiryTemplateSimulateStepsNavigation = ({
  currentStepName,
  steps,
  onSelectStep,
}: InquiryTemplateSimulateStepsNavigationProps) => {
  const navItemContainerRef = useRef<HTMLDivElement>(null);

  const navContainerWidth =
    navItemContainerRef?.current != null
      ? navItemContainerRef.current.getBoundingClientRect().width -
        parseInt(getComputedStyle(navItemContainerRef.current).paddingLeft) * 4
      : 0;

  // Subtle effort here to highlight the selected step when skipping step. This is needed because
  // when we skip step, we don't immediately update the `currentStepName` to that selected step (it is
  // updated only when that new step finishes loading).
  //
  // Therefore, we need to show a different highlighted step in the steps nav that is not `currentStepName`
  // To do so, we keep a `selectedStepName` state which we use to determine which step is highlighted. When we
  // start to skip step, we update `selectedStepName` accordingly.
  //
  // However, we also need an effect to update `selectedStepName` when `currentStepName` changes to accommodate
  // natural non-skip transitions that don't explicitly set `selectedStepName`
  const [selectedStepName, setSelectedStepName] = useState(currentStepName);
  useEffect(() => {
    setSelectedStepName(currentStepName);
  }, [currentStepName]);

  return (
    <Container aria-label='Inquiry template steps' width={`${InquiryTemplateStepsPanelWidth}px`}>
      <NavigationHeaderContainer>
        Steps
        <Spacer height={2} />
        <Paragraph>
          The name of the current step shown in the Inquiry is highlighted here. You can skip to any
          step by selecting it in this panel.
        </Paragraph>
      </NavigationHeaderContainer>
      <NavigationItemsContainer ref={navItemContainerRef}>
        {steps.map((step) => {
          return (
            <StepNavItem
              id={step.name}
              key={step.name}
              step={step}
              active={step.name === selectedStepName}
              containerWidth={navContainerWidth}
              onClick={() => {
                if (step.name !== currentStepName) {
                  onSelectStep(step.name);
                  setSelectedStepName(step.name);
                }
              }}
            />
          );
        })}
      </NavigationItemsContainer>
    </Container>
  );
};
